import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '~/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    guestOnly?: boolean;
    devOnly?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('~/pages/home/index.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('~/pages/login/index.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('~/pages/forgot-password/index.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('~/pages/reset-password/index.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/activate',
    name: 'activate',
    component: () => import('~/pages/activate/index.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/app',
    name: 'app-dashboard',
    component: () => import('~/pages/app-dashboard/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/settings',
    name: 'app-settings',
    component: () => import('~/pages/settings/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/settings/2fa',
    name: 'app-settings-2fa',
    component: () => import('~/pages/settings-2fa/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/admin/users',
    name: 'app-admin-users',
    component: () => import('~/pages/admin-users/index.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/app/admin/audit-log',
    name: 'app-admin-audit-log',
    component: () => import('~/pages/admin-audit-log/index.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/app/products',
    name: 'app-products',
    component: () => import('~/pages/products/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/products/:id',
    name: 'app-product-form',
    component: () => import('~/pages/product-form/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/orders',
    name: 'app-orders',
    component: () => import('~/pages/orders/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/orders/:id',
    name: 'app-order-detail',
    component: () => import('~/pages/order-detail/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/customers',
    name: 'app-customers',
    component: () => import('~/pages/customers/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/customers/:id',
    name: 'app-customer-detail',
    component: () => import('~/pages/customer-detail/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/questions',
    name: 'app-questions',
    component: () => import('~/pages/questions/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/app/reports',
    name: 'app-reports',
    component: () => import('~/pages/reports/index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/_dev/components',
    name: 'dev-components',
    component: () => import('~/pages/dev-components/index.vue'),
    meta: { devOnly: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('~/pages/not-found/index.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    if (saved) return saved;
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.loaded) {
    await auth.hydrate();
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'home' };
  }
  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'app-dashboard' };
  }
  return true;
});
